import {
  Box,
  Button,
  Chip,
  Divider,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { isEmpty, isNil } from "lodash";
import { useSnackbar } from "notistack";
import pluralize from "pluralize";
import React from "react";
import { UseQueryResult } from "react-query";

import HarvestAPI from "../api/Harvest.api";
import LotAPI from "../api/Lot.api";
import SublotGroupAPI from "../api/SublotGroup.api";
import { Harvest, Lot, Sublot } from "../types";
import Autosave from "./Autosave";
import SearchBarAutocomplete from "./SearchBarAutocomplete";

type Props = {
  lot?: Lot;
  setLot: React.Dispatch<React.SetStateAction<Lot | Sublot | undefined>>;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  harvest: Harvest;
  refetchHarvest: () => Promise<UseQueryResult>;
};

export default function SearchForSublotGroup({
  lot,
  setLot,
  open,
  setOpen,
  harvest,
  refetchHarvest,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [query, setQuery] = React.useState<string>("");
  const [newSublotNumber, setNewSublotNumber] = React.useState<string>("");
  const [newSublotAcres, setNewSublotAcres] = React.useState<number>(1);

  const [sublot, setSublot] = React.useState<Sublot | undefined>(
    // @ts-ignore
    harvest.lotableType === "Sublot" ? harvest.lotable : undefined,
  );

  const { mutateAsync: updateHarvest } = HarvestAPI.useUpdate();

  const { mutateAsync: createSublotAsync } =
    SublotGroupAPI.useCreateSublotInSublotGroup({
      sublotGroupId: sublot?.sublotGroupId,
      lotNum: newSublotNumber,
      numOfAcres: newSublotAcres,
    });

  const { data: remainingAcresData } = SublotGroupAPI.useRemainingAcres({
    sublotGroupId: sublot?.sublotGroupId,
  });

  const { data } = LotAPI.useSublotSearch({ query, lotId: lot?.id });

  const options = data
    ? data.map((o) => ({
        label: `${o.num} has ${o.sublotsInGroup - 1} other ${pluralize(
          "sublot",
          o.sublotsInGroup - 1,
        )}`,
        value: o,
      }))
    : [];

  const value =
    isNil(sublot) || isEmpty(sublot)
      ? { label: query, value: query }
      : {
          label: `${sublot.num} has ${
            sublot.sublotsInGroup - 1
          } other ${pluralize("sublot", sublot.sublotsInGroup - 1)}`,
          value: sublot,
        };

  const handleSave = async () => {
    try {
      const newHarvest = await updateHarvest({
        id: harvest.id,
        lotableId: sublot === undefined ? lot?.id : sublot?.id,
        lotableType: sublot === undefined && lot?.id ? "Lot" : "Sublot",
      });
      console.log("newHarvest", newHarvest);
      setLot(newHarvest.lot);
      enqueueSnackbar("Successfully split harvest into sublots.", {
        variant: "success",
      });
      await refetchHarvest();
      setOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreateNewSublotInGroup = async () => {
    try {
      if (!(newSublotNumber && newSublotNumber.length > 0)) {
        enqueueSnackbar(`Must have a sublot number selected`, {
          variant: "error",
        });
        return;
      }
      if (
        !(
          remainingAcresData &&
          remainingAcresData["remainingAcres"] &&
          newSublotAcres &&
          newSublotAcres > 0 &&
          newSublotAcres < remainingAcresData["remainingAcres"]
        )
      ) {
        enqueueSnackbar(
          // @ts-ignore
          `Must be between 0 & ${remainingAcresData["remainingAcres"]}`,
          {
            variant: "error",
          },
        );
        return;
      }
      const newSublot = await createSublotAsync();
      setSublot(newSublot);
      enqueueSnackbar("Successfully created new Sublot", {
        variant: "success",
      });
      const newHarvest = await updateHarvest({
        id: harvest.id,
        lotableId: newSublot.id,
        lotableType: "Sublot",
      });
      setOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <Box
        sx={{
          position: "absolute" as "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          border: "2px solid #000",
          boxShadow: 24,
          p: 4,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="h6">Search for Sublot Group</Typography>

        <SearchBarAutocomplete
          label={"Sublot"}
          inputValue={query}
          setSearchValue={setQuery}
          options={options}
          noOptionsText={"SubLot not found."}
          value={value}
          onChange={(event, newValue) => {
            if (isNil(newValue) || isEmpty(newValue)) {
              setSublot(undefined);
            } else {
              setSublot(newValue.value);
            }
          }}
        />

        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>

        <Divider>
          <Chip label="Create new Sublot" />
        </Divider>

        {sublot ? (
          <React.Fragment>
            <TextField
              label="New Sublot Number"
              value={newSublotNumber}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setNewSublotNumber(event.target.value);
              }}
            />
            <TextField
              label="New Sublot Number of Acres"
              value={newSublotAcres}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setNewSublotAcres(parseFloat(event.target.value));
              }}
              type="number"
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ width: "100%" }}
              helperText={`Acres remaining in lot: ${
                remainingAcresData && remainingAcresData["remainingAcres"]
              }`}
            />

            <Button
              fullWidth
              variant="outlined"
              onClick={handleCreateNewSublotInGroup}
            >
              Create new Sublot in Group
            </Button>
          </React.Fragment>
        ) : (
          <Typography variant="body1">
            Select a Sublot in order to create a new sublot in same grouping
          </Typography>
        )}
      </Box>
    </Modal>
  );
}
